import { data } from '@portal/pkg-api/shared'
import { UserStorageApi } from '@portal/pkg-api/navigator'
import { UserStorage, UserStorageKey } from '@portal/pkg-api/gen/navigator'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getUserNotificationStore } from '@migrate/stores'
import { UserNotificationType } from '@migrate/stores/UserNotificationStore'

const prefix = 'navigator.userStorage'

export const UserStorageQueries = {
  get: (key: UserStorageKey) => ({
    queryKey: [`${prefix}.get`, key],
    queryFn: () => data(UserStorageApi.getUserStorageByKey(key)),
  }),
}

export const useUserStorage = (key: UserStorageKey) => {
  return useQuery(UserStorageQueries.get(key))
}

// User storage holds anything a caller keeps per user, so only the caller can say what failed.
const reportFailure = (
  attempted: 'save' | 'delete',
  key: UserStorageKey,
  error: unknown,
  failureMessage: string,
) => {
  console.error(`Failed to ${attempted} user storage ${key}:`, error)
  getUserNotificationStore().addNotification(UserNotificationType.ERROR, failureMessage, {
    notifierOptions: { persist: false },
  })
}

/**
 * @param failureMessage an `ns:key` translation key, such as `navigator:…`, of the message shown
 *   when the save fails. It should name what the caller was saving, and closes by itself.
 */
export const useUpdateUserStorage = (failureMessage: string) => {
  const queryClient = useQueryClient()

  return useMutation<unknown, unknown, UserStorage>({
    mutationFn: (value) =>
      UserStorageApi.updateUserStorage(value, { mercatusNotifyOnError: false }),
    onSuccess: (_data, { key }) => {
      void queryClient.invalidateQueries(UserStorageQueries.get(key))
    },
    onError: (error, { key }) => reportFailure('save', key, error, failureMessage),
  })
}

/**
 * @param failureMessage an `ns:key` translation key, such as `navigator:…`, of the message shown
 *   when the delete fails. It should name what the caller was deleting, and closes by itself.
 */
export const useDeleteUserStorage = (failureMessage: string) => {
  const queryClient = useQueryClient()

  return useMutation<unknown, unknown, UserStorageKey>({
    mutationFn: (key: UserStorageKey) =>
      UserStorageApi.deleteUserStorage(key, { mercatusNotifyOnError: false }),
    onSuccess: (_data, key: UserStorageKey) => {
      void queryClient.invalidateQueries(UserStorageQueries.get(key))
    },
    onError: (error, key) => reportFailure('delete', key, error, failureMessage),
  })
}
